<script context="module" lang="ts">
  export const sideMenuWidth = writable<number>();
</script>

<script lang="ts">
  import { link, location } from "svelte-spa-router";
  import IntegrationIcon from "../icons/IntegrationIcon.svelte";
  import InvoicesIcon from "../icons/InvoicesIcon.svelte";
  import NavCollapseIcon from "../icons/NavCollapseIcon.svelte";
  import NavExpandIcon from "../icons/NavExpandIcon.svelte";
  import PricesIcon from "../icons/PricesIcon.svelte";
  import RecipesIcon from "../icons/RecipesIcon.svelte";
  import BranchSelector from "./BranchSelector.svelte";
  import SettingsButton from "./SettingsButton.svelte";
  import SideMenuLink from "./SideMenuLink.svelte";

  import { getIntegrations } from "../../helpers/integrations-api";
  import { activeIntegrationsStore } from "../../stores/integrations-store";
  import SideMenuLogo from "../icons/SideMenuLogo.svelte";
  import { WHITELISTED_FOR_RECIPE_COSTING } from "../../helpers/constants";
  import BarChartIcon from "../icons/BarChartIcon.svelte";
  import GpIcon from "../icons/GpIcon.svelte";
  import { track } from "../../helpers/mixpanel";
  import { breakpoints } from "../../theme/breakpoints";
  import { writable } from "svelte/store";
  import { t } from "../../helpers/translations";
  import { SELECTED_BUYER_URL_PARAM_KEY, selectedBuyer } from "../../stores/buyer";
  import type { Buyer } from "../../helpers/types";

  let isExpanded = true;
  let activeIntegrations: string[] = [];
  let windowWidth: number;
  let windowWidthOnLastToggled: number = window.innerWidth;
  $: isWindowSizeIncreasing = windowWidth > windowWidthOnLastToggled;

  function toggleExpandMenu() {
    isExpanded = !isExpanded;
    windowWidthOnLastToggled = windowWidth;
    if (!isExpanded) {
      track("nav_collapse_click");
    }
  }

  $: isIntegrationEnabled = (integration: string) => {
    if (activeIntegrations.length === 0) return true;

    if (activeIntegrations.includes(integration)) return true;

    return false;
  };

  activeIntegrationsStore.subscribe((value) => {
    activeIntegrations = value.map((i) => i.platform);
  });

  $: if ($selectedBuyer?.id) {
    getIntegrations($selectedBuyer.id);
  }

  $: expandedMenu = isExpanded;

  function handleXeroButtonClick() {
    track("nav_integration_icon_quickbooks_click");
  }

  function handleQuickBooksButtonClick() {
    track("nav_integration_icon_xero_click");
  }

  function buildLink(path: string, buyer: Buyer | null) {
    const params = new URLSearchParams();
    if (buyer) {
      params.set(SELECTED_BUYER_URL_PARAM_KEY, buyer.id);
    }

    return params.size ? `/${path}?${params.toString()}` : `/${path}`;
  }

  $: if (windowWidth < breakpoints.xl && !isWindowSizeIncreasing) {
    isExpanded = false;
  } else if (windowWidth >= breakpoints.xl) {
    isExpanded = true;
  }
</script>

<div class={`main ${isExpanded ? "expanded" : "collapsed"}`} bind:clientWidth={$sideMenuWidth}>
  <div class="logo" class:logo-collapsed={!isExpanded}>
    <SideMenuLogo expanded={isExpanded} />
  </div>

  <div class="branch-selector">
    <BranchSelector isPanelExpanded={isExpanded} />
  </div>

  <div class="linksContainer">
    <SideMenuLink
      href={buildLink("spend-report", $selectedBuyer)}
      text={t("menu.overview.title")}
      sidePanelExpanded={expandedMenu}
      selected={$location === "/spend-report"}
    >
      <BarChartIcon slot="icon" />
    </SideMenuLink>

    <SideMenuLink
      href={buildLink("report", $selectedBuyer)}
      text={t("menu.invoices.title")}
      sidePanelExpanded={expandedMenu}
      selected={$location === "/report"}
    >
      <InvoicesIcon slot="icon" />
    </SideMenuLink>

    <!-- Hide stock for now - paid feature -->
    <!-- <SideMenuLink
      href="/stock-take"
      text="stock management"
      sidePanelExpanded={expandedMenu}
      selected={$location === "/stock-take"}
      marginOffset={4}
    >
      <StockIcon slot="icon" />
    </SideMenuLink> -->

    {#if $selectedBuyer && WHITELISTED_FOR_RECIPE_COSTING.includes($selectedBuyer?.id)}
      <SideMenuLink
        href={buildLink("recipes", $selectedBuyer)}
        text={t("menu.recipes.title")}
        sidePanelExpanded={expandedMenu}
        selected={$location === "/recipes"}
      >
        <RecipesIcon slot="icon" />
      </SideMenuLink>
    {/if}

    <SideMenuLink
      href={buildLink("prices", $selectedBuyer)}
      text={t("menu.priceHistory.title")}
      sidePanelExpanded={expandedMenu}
      selected={$location === "/prices"}
    >
      <PricesIcon slot="icon" />
    </SideMenuLink>

    <SideMenuLink
      href={buildLink("gp", $selectedBuyer)}
      text={t("menu.gp.title")}
      sidePanelExpanded={expandedMenu}
      selected={$location === "/gp"}
    >
      <GpIcon slot="icon" />
    </SideMenuLink>

    <SideMenuLink
      href={buildLink("integrations", $selectedBuyer)}
      text={t("menu.integrations.title")}
      selected={$location === "/integrations"}
      sidePanelExpanded={expandedMenu}
    >
      <IntegrationIcon slot="icon" />
    </SideMenuLink>
  </div>

  <button
    title={`${expandedMenu ? "Hide" : "Show"} main menu`}
    class="menu-expand-btn"
    on:click={toggleExpandMenu}
  >
    {#if expandedMenu}
      <NavCollapseIcon />
    {:else}
      <NavExpandIcon />
    {/if}
  </button>

  <SettingsButton isMenuExpanded={expandedMenu} />
</div>

<svelte:window bind:innerWidth={windowWidth} />

<style>
  .main {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    background: #fff;
    height: 100vh;
    padding: 16px 8px;
    margin-right: 4px;
    box-sizing: border-box;
    box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.05);
  }
  .expanded {
    min-width: 280px;
    max-width: 280px;
  }
  .collpased {
    min-width: 72px;
    max-width: 72px;
  }

  .logo {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    padding-left: 8px;
    padding-top: 8px;
  }

  .logo-collapsed {
    padding-left: 4px;
  }

  .linksContainer {
    margin-top: 16px;
  }

  button {
    background: none;
    outline: none;
    border: none;
    color: var(--rekki-dark);
    opacity: 0.6;
  }
  button:hover {
    opacity: 1;
  }

  .disabled {
    opacity: 0.3;
    pointer-events: none;
  }
  .menu-expand-btn {
    flex-grow: 1;
    display: flex;
    align-items: flex-end;
  }

  .branch-selector {
    margin: 8px 0 16px;
  }

  .submenu {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }

  .integration-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 6px;
    padding: 10px 12px;
    border: solid 1px var(--rk-divider-0);
    border-radius: 8px;
  }
</style>
